import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ColorService } from '@app/shared/services/color.service';
import { HighchartsService } from '@app/shared/services/highcharts.service';
import { TranslateService } from '@ngx-translate/core';
import { TagSignalHistory } from '@app/profile/profile-api.model';
import { TagsApi } from '@app/tags/tag-api.model';
import { Chart, Point } from 'highcharts';
import { DatePipe } from '@angular/common';
import { SanitizationService } from '@app/shared/services/sanitization.service';
import { LocalizationService } from '@app/shared/localization.service';

@Component({
  selector: 'dgx-signal-history-chart',
  templateUrl: './signal-history-chart.component.html',
  styleUrls: ['./signal-history-chart.component.scss'],
})
export class SignalHistoryChartComponent implements OnChanges {
  @Input() public signalHistories: TagSignalHistory[];
  @Input() public historyXAxisLabels: string[];
  @Input() public skills: TagsApi.Tag[];
  @Input() public currentSkill: string;
  @Input() public maxSkills: number;

  private chart: Chart;
  private currentChartPoint: Point;
  private colorList = ['orange', 'pink', 'ocean', 'forest', 'purple', 'sky'];
  public chartColors = this.colorList.map((c) => this.colors.getColor(c));

  public i18n = this.translate.instant([
    'Core_NoResults',
    'dgProfileOverview_TooltipSignalsSingle',
    'dgProfileOverview_TooltipSignalsPlural',
  ]);

  constructor(
    private element: ElementRef,
    private Highcharts: HighchartsService,
    private colors: ColorService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private sanitizationService: SanitizationService,
    private localizationService: LocalizationService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.signalHistories || changes.skills) {
      this.generateChart();
    }

    const hasCurrentSkillChanged =
      changes?.currentSkill?.currentValue !==
      changes?.currentSkill?.previousValue;
    if (hasCurrentSkillChanged) {
      this.selectPoint(this.currentSkill);
    }
  }

  private hasSignalsForSkill(skillName: string): boolean {
    const skillId = this.skills.find((s) => s.title === skillName).tagId;
    return !!this.signalHistories.find((history) => history.tagId === skillId);
  }

  private selectPoint(skillName: string = ''): void {
    if (skillName) {
      const series = this.chart.series.find((s) => s.name === skillName);
      this.currentChartPoint = series?.data[series.data.length - 1];
      this.chart.tooltip.refresh(this.currentChartPoint);
    } else {
      this.chart.tooltip.hide(0);
      this.chart.series.forEach((s) => {
        s.setState('normal');
      });
      this.currentChartPoint?.setState('normal');
    }
  }

  private generateChart(): void {
    const defaultData = this.historyXAxisLabels.map((l) => 0);
    const series = this.skills
      .slice(0, this.maxSkills)
      .reduce((chartData, s, i) => {
        if (this.hasSignalsForSkill(s.title)) {
          chartData.push({
            name: this.sanitizationService.cleanHtml(s.title),
            data: this.signalHistories.find((hist) => hist.tagId === s.tagId)
              .history,
            color: this.chartColors[i],
          });
        } else {
          chartData.push({
            name: this.sanitizationService.cleanHtml(s.title),
            data: defaultData,
            color: this.chartColors[i],
          });
        }
        return chartData;
      }, []);

    const container = this.element.nativeElement.querySelector(
      '.highcharts-line-signals'
    );

    const getSignalsLabel = (y) => {
      return this.translate.instant(
        y > 1
          ? 'dgProfileOverview_TooltipSignalsPlural'
          : 'dgProfileOverview_TooltipSignalsSingle'
      );
    };
    const getTranslatedMonth = (monthNumber) => {
      // We don't care about the actual date value here, as long as it's a valid date. So we sub the month number into a valid
      // then use the datePipe to extract the translated, short month abbreviation.
      return this.datePipe.transform(
        `2023-${monthNumber}-01`,
        'MMM',
        undefined,
        this.localizationService.userLocale
      );
    };

    const monthTranslations = this.historyXAxisLabels.reduce(
      (allMonths, month) => {
        allMonths.push(getTranslatedMonth(month));
        return allMonths;
      },
      []
    );
    this.Highcharts.createChart(container, {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'inherit',
        },
      },
      exporting: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: monthTranslations,
        tickWidth: 0,
        lineColor: this.colors.getColor('ebony-a08'),
        gridLineColor: this.colors.getColor('ebony-a08'),
        labels: { style: { color: this.colors.getColor('ebony-a61') } },
      },
      yAxis: {
        floor: 0,
        min: 0,
        minRange: 4,
        minTickInterval: 1,
        lineWidth: 0,
        allowDecimals: false,
        title: {
          text: '',
        },
        labels: { style: { color: this.colors.getColor('ebony-a61') } },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: this.colors.getColor('white'),
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        formatter: function () {
          const signalsLabel = getSignalsLabel(this.y);
          return `
              <div class="highcharts__tooltip guts-p-v-half guts-p-h-2">
                  <div class="highcharts__tooltip__label">${this.x}</div>
                  <div class="l_flex break">${this.series.name}</div>  <div>${this.y} ${signalsLabel}</div>
              </div>
          `;
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        line: {
          marker: {
            fillColor: '#FFFFFF', // should match the chart background
            lineColor: null,
            lineWidth: 2,
            radius: 3,
            symbol: 'circle',
            states: {
              hover: {
                // Dirty hack to make the points filled using the current line-color
                radius: 2,
                lineWidth: 4,
              },
            },
          },
          states: {
            hover: {
              halo: false,
              lineWidthPlus: 0, // don't make the line thicker on hover
            },
          },
        },
      },
      series,
    }).subscribe((chart) => (this.chart = chart));
  }
}
